import {organizationApiClient} from 'organization/organizationApiClient'
import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {FullPageLoader} from 'organization/theme/FullPageLoader'
import {useOrganization} from 'organization/providers/OrganizationProvider'

export type PayoutPreview = {
  grossPayout: number
  invoiceSubtotal: number
  invoiceVat: number
  invoiceTotal: number
  implicitVatDeductedPercent: number | null
}

function mapPayoutPreview(data: unknown): PayoutPreview {
  if (typeof data === 'number') {
    return {
      grossPayout: data,
      invoiceSubtotal: data,
      invoiceVat: 0,
      invoiceTotal: data,
      implicitVatDeductedPercent: null,
    }
  }
  const o = data as Record<string, unknown>
  return {
    grossPayout: Number(o.gross_payout ?? 0),
    invoiceSubtotal: Number(o.invoice_subtotal ?? o.gross_payout ?? 0),
    invoiceVat: Number(o.invoice_vat ?? 0),
    invoiceTotal: Number(o.invoice_total ?? o.gross_payout ?? 0),
    implicitVatDeductedPercent:
      o.implicit_vat_deducted_percent === null ||
      o.implicit_vat_deducted_percent === undefined
        ? null
        : Number(o.implicit_vat_deducted_percent),
  }
}

export interface EarningInvoiceContextProps {
  payoutPreview: PayoutPreview
  issueInvoice: (invoiceNumber: string) => Promise<boolean>
  refresh: () => Promise<void>
  loading: boolean
}

export const EarningInvoiceContext = React.createContext<
  EarningInvoiceContextProps | undefined
>(undefined)

export default function EarningInvoiceProvider(props: {
  children: React.ReactElement
}) {
  const apiClient = organizationApiClient()

  const [payoutPreview, setPayoutPreview] = useState<PayoutPreview>({
    grossPayout: 0,
    invoiceSubtotal: 0,
    invoiceVat: 0,
    invoiceTotal: 0,
    implicitVatDeductedPercent: null,
  })
  const [loading, setLoading] = useState<boolean>(true)
  const {organization} = useOrganization()

  useEffect(() => {
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const get = async () => {
    const response = await apiClient.get(
      `api/organization/${organization?.id}/earning/new-sales`,
    )
    setPayoutPreview(mapPayoutPreview(response))
    setLoading(false)
  }

  const refresh = async () => {
    await get()
  }

  const issueInvoice = async (invoiceNumber: string) => {
    const result = await apiClient
      .post(
        `/api/organization/${organization?.id}/earning/create-payout-invoice`,
        {
          invoice_number: invoiceNumber,
        },
      )
      .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })

    return result
  }

  return (
    <EarningInvoiceContext.Provider
      value={{
        payoutPreview,
        refresh,
        loading,
        issueInvoice,
      }}
    >
      {loading && <FullPageLoader />}
      {!loading && props.children}
    </EarningInvoiceContext.Provider>
  )
}

export function useEarningInvoice() {
  const context = React.useContext(EarningInvoiceContext)
  if (context === undefined) {
    throw new Error(
      'useEarningInvoice must be used within EarningInvoiceProvider',
    )
  }
  return context
}
